import React, { useState, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEye, faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import { faBlockBrick, faBuildingUser, faHome, faListCheck, faPenRuler, faSunCloud, faHyphen } from '@fortawesome/pro-light-svg-icons';
import { toast } from "react-toastify";
import Button from "@/components/buttons/button";
import useDashboardProcess from '../../../../../hooks/use-dashboard-process.ts';
import MetricProcess from '../models/metric-process.ts';
import useJobAsync from '../../../../../hooks/jobs/use-job-async.ts';
import useProjectContext from '../../../../../services/context/project/use-project-context.ts';
import useProjectFormNavigationContext from '../../../context/project-form-navigation/use-project-form-navigation-context.ts';
import ProjectEditButton from "@/components/buttons/project-edit-button";
import Card from '../../../../../components/card';
import ExternalLinkButton from "../../../../../components/buttons/external-link-button";
import LinkButton from "../../../../../components/buttons/link-button";
import ENDPOINTS from "../../../../../services/endpoints.ts";
import JobDto, { prodTaskAdapter } from '../../../../../models/interfaces/dtos/production/job-dto.ts';
import useProjectMeasureSync from '../../measures/hooks/use-project-measure-sync.ts';
import FlowMode from '@/models/enums/flow-mode.ts';
import useProjectProcessContext from '@/services/context/project-process/use-project-process-context';

const TakeOffProcesses: React.FC = () => {
    const { setRoute } = useProjectFormNavigationContext();
    const { project } = useProjectContext();
    const { isProcessRunning, beginProcess } = useProjectProcessContext();
    const { updateProjectSpaces, updateProjectOpenings, updateProjectWalls, updateProjectSlabs } = useProjectMeasureSync();
    const projectId = project.id;

    const { dashboardProcessStatusQuery, applyDashboardProcessStatus } = useDashboardProcess(projectId);

    const [isProcessing, setIsProcessing] = useState(false);

    const disableProcessing = useCallback(() => {
        setIsProcessing(false);
    }, [setIsProcessing]);

    const { launchJob: launchProdJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "Calcul de Surfaces - ProdAPI", false),
        processName: "Calcul de Surfaces - ProdAPI",
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,
        onJobDone: disableProcessing,
        onError: disableProcessing
    });

    const handleUpdateProjectSurfaces = useCallback(async () => {
        const finishProcess = beginProcess();
        const toastId = toast.loading("Mise à jour du projet...", {
            autoClose: false, closeButton: false, closeOnClick: false,
        });

        try {
            await updateProjectWalls.mutateAsync(projectId);
            await updateProjectOpenings.mutateAsync(projectId);
            await updateProjectSpaces.mutateAsync(projectId);
            await updateProjectSlabs.mutateAsync(projectId);
            toast.update(toastId, {
                render: "La mise à jour du projet est terminée.",
                type: "success", isLoading: false, autoClose: 5000, closeButton: true,
            });
            await launchProdJob();
        } catch (error) {
            toast.update(toastId, {
                render: `Erreur: ${error}`,
                type: "error", isLoading: false, autoClose: false, closeButton: true,
            });
        } finally {
            finishProcess();
            disableProcessing();
        }
    }, [beginProcess, projectId, updateProjectWalls, updateProjectOpenings, updateProjectSpaces, updateProjectSlabs, launchProdJob, disableProcessing]);

    /* MUTATIONS 2D */
    // const { launchJob: prepareHull2DJob } = useJobAsync<JobDto>({
    //     jobUrl: ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "Calcul de Surfaces - ProdAPI"),
    //     getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
    //     getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
    //     adapter: prodTaskAdapter,
    //     onJobDone: disableProcessing,
    //     onError: disableProcessing
    // });
    // const { launchJob: calculSurface2DJob } = useJobAsync<JobDto>({
    //     jobUrl: ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "BAAMER :: Export : Promoter Surfaces (GOe)"),
    //     getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
    //     adapter: prodTaskAdapter,
    //     onJobDone: disableProcessing,
    //     onError: disableProcessing
    // });
    const { launchJob: calculCarbonMetric2DJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "Carbone - ProdAPI", false),
        processName: "Carbone - ProdAPI",
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,
        onJobDone: disableProcessing,
        onError: disableProcessing
    });
    const { launchJob: calculQuantitatifMEX2DJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "BOOMER :: Export : MEX", false),
        processName: "BOOMER :: Export : MEX",
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,
        onJobDone: disableProcessing,
        onError: disableProcessing
    });
    const { launchJob: calculQuantitatifMenuiserie2DJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "BOOMER :: Export : Doors and Windows", false),
        processName: "BOOMER :: Export : Doors and Windows",
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,
        onJobDone: disableProcessing,
        onError: disableProcessing
    });
    const { launchJob: layoutApartment2DJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "Appartements - ProdAPI", false),
        processName: "Appartements - ProdAPI",
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,
        onJobDone: disableProcessing,
        onError: disableProcessing
    });
    const { launchJob: preciseGrosOeuvreSurface2DJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "Second Oeuvre - ProdAPI", false),
        processName: "Second Oeuvre - ProdAPI",
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,
        onJobDone: disableProcessing,
        onError: disableProcessing
    });

    /* MUTATIONS 3D */
    // const { launchJob: calculSurface3DJob } = useJobAsync<JobDto>({
    //     jobUrl: ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "BAAMER :: Export : Promoter Surfaces (GOe)"),
    //     getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
    //     adapter: prodTaskAdapter,
    //     onJobDone: disableProcessing,
    //     onError: disableProcessing
    // });
    const { launchJob: calculCarbonMetric3DJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "Carbone - ProdAPI", false),
        processName: "Carbone - ProdAPI",
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,
        onJobDone: disableProcessing,
        onError: disableProcessing
    });
    const { launchJob: calculQuantitatifMEX3DJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "BOOMER :: Export : MEX", false),
        processName: "BOOMER :: Export : MEX",
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,
        onJobDone: disableProcessing,
        onError: disableProcessing
    });
    const { launchJob: calculQuantitatifMenuiserie3DJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "BOOMER :: Export : Doors and Windows", false),
        processName: "BOOMER :: Export : Doors and Windows",
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,
        onJobDone: disableProcessing,
        onError: disableProcessing
    });
    const { launchJob: layoutApartment3DJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "Appartements - ProdAPI", false),
        processName: "Appartements - ProdAPI",
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,
        onJobDone: disableProcessing,
        onError: disableProcessing
    });
    const { launchJob: preciseGrosOeuvreSurface3DJob } = useJobAsync<JobDto>({
        jobUrl: () => ENDPOINTS.PRODUCTION_LAUNCH_PROCESS(projectId, "Second Oeuvre - ProdAPI", false),
        processName: "Second Oeuvre - ProdAPI",
        getExistingJobUrl: ENDPOINTS.PRODUCTION_GET_PREVIOUSLY_LAUNCHED(projectId),
        getStatusUrl: ENDPOINTS.PRODUCTION_GET_JOB_STATUS,
        adapter: prodTaskAdapter,
        onJobDone: disableProcessing,
        onError: disableProcessing
    });

    const wallsJob = useCallback(() => {
        const searchParams = new URLSearchParams([["launchPlanDetectionJobs", "true"]]);
        setRoute('measures', searchParams);
    }, [setRoute]);

    const hullsValidation = useCallback(() => {
        setRoute('measures');
    }, [setRoute]);

    const MetricList: MetricProcess[] = useMemo(() => {
        const allowed2DInBasicMode = new Set([
            'PrepareMurs',
            'PrepareHulls',
            'HullsDestination',
            'Surfaces'
        ]);

        const baseMetrics = [
            new MetricProcess('PrepareMurs',  project.flowMode, { icon: faBlockBrick, text: 'Saisie des murs et ouvertures', is3DDone: undefined, _2DAction: wallsJob }),
            new MetricProcess('PrepareHulls', project.flowMode, { icon: faHome, text: 'Préparation des coques', _2DAction: handleUpdateProjectSurfaces }),
            new MetricProcess('HullsDestination', project.flowMode, { icon: faListCheck, text: 'Destination des coques', isMagick: false, actionText: 'Validez', _2DAction: hullsValidation, _3DAction: hullsValidation, /* visualisationLink: `/takeoff/${projectId}?tab=validation&review` */ }),
            new MetricProcess('Surfaces', project.flowMode, { icon: faHome, text: 'Surfaces', _2DAction: hullsValidation, _3DAction: hullsValidation, visualisationLink: `${import.meta.env.VITE_BOOMER_URL}/Management/Surfaces/${project.projectBoomerId}` }),
            new MetricProcess('QCarbonMetric', project.flowMode, { icon: faSunCloud, text: 'Surfaces inter-étages', _2DAction: calculCarbonMetric2DJob, _3DAction: calculCarbonMetric3DJob, visualisationLink: `${import.meta.env.VITE_BOOMER_URL}/Carbone/Surfaces/${project.projectBoomerId}` }),
            new MetricProcess('QGrosOeuvres', project.flowMode, { icon: faPenRuler, text: 'Quantitatif Lot Gros-Œuvre', visualisationLink: `${import.meta.env.VITE_BOOMER_URL}/Purchase/LotsMasonry/${project.projectBoomerId}` }),
            new MetricProcess('QMex', project.flowMode, { icon: faPenRuler, text: 'Quantitatif Lot MEX', _2DAction: calculQuantitatifMEX2DJob, _3DAction: calculQuantitatifMEX3DJob, visualisationLink: `${import.meta.env.VITE_BOOMER_URL}/Purchase/ExternalCarpentry/${project.projectBoomerId}` }),
            new MetricProcess('QMenuiserie', project.flowMode, { icon: faPenRuler, text: 'Quantitatif Lot Menuiseries', _2DAction: calculQuantitatifMenuiserie2DJob, _3DAction: calculQuantitatifMenuiserie3DJob, visualisationLink: `${import.meta.env.VITE_BOOMER_URL}/Purchase/LotsJoinery/${project.projectBoomerId}` }),
            new MetricProcess('ApartmentLayout', project.flowMode, { icon: faBuildingUser, text: 'Aménagements des appartements', _2DAction: layoutApartment2DJob, _3DAction: layoutApartment3DJob, visualisationLink: `${import.meta.env.VITE_BOOMER_URL}/Apartment/Surfaces/${project.projectBoomerId}` }),
            new MetricProcess('GrosOeuvreAffinées', project.flowMode, { icon: faBuildingUser, text: 'Surfaces affinées avec le Second-Œuvre', _2DAction: preciseGrosOeuvreSurface2DJob, _3DAction: preciseGrosOeuvreSurface3DJob, visualisationLink: `${import.meta.env.VITE_BOOMER_URL}/Management/Surfaces/${project.projectBoomerId}` }),
        ];

        const adaptedMetrics = baseMetrics.map(metric => {
            // Mode 2D3D simple : on désactive certains boutons 2D
            if (project.flowMode === FlowMode._2D3D) {
                if (!allowed2DInBasicMode.has(metric.keyName)) {
                    return {
                        ...metric,
                        is2D: false
                    };
                }
            }

            // Mode 2D3D avancé : on active tous les boutons 2D et on remet is3D à false puisque par défaut est égal à true, comme ça on désactive ceux de 3D
            if (project.flowMode === FlowMode._2D3DAdvanced) {
                return {
                    ...metric,
                    is2D: true,
                    is3D: false
                };
            }

            return metric;
        });

        return applyDashboardProcessStatus(adaptedMetrics, project.flowMode, dashboardProcessStatusQuery.data ?? undefined);
    }, [dashboardProcessStatusQuery.data, applyDashboardProcessStatus, calculCarbonMetric2DJob, calculCarbonMetric3DJob, calculQuantitatifMEX2DJob, calculQuantitatifMEX3DJob, calculQuantitatifMenuiserie2DJob, calculQuantitatifMenuiserie3DJob, hullsValidation, layoutApartment2DJob, layoutApartment3DJob, preciseGrosOeuvreSurface2DJob, preciseGrosOeuvreSurface3DJob, handleUpdateProjectSurfaces, project.projectBoomerId, project.flowMode, wallsJob]);

    return (
        <div className='flex flex-1 items-center justify-center'>
            <Card additionalClasses="overflow-hidden">
                <table className='divide-y divide-gray-300 table-auto'>
                    <thead>
                        <tr className='divide-x divide-gray-200'>
                            <th scope='col' className='p-4 text-left text-sm font-semibold text-gray-900 w-160'>
                                Process
                            </th>
                            <th scope='col' className='p-4 text-left text-sm font-semibold text-gray-900'>
                                Plans PDF
                            </th>
                            <th scope='col' className='p-4 text-left text-sm font-semibold text-gray-900'>
                                Maquette 3D
                            </th>
                            <th scope='col' className='p-4 text-left text-sm font-semibold text-gray-900'>
                                Visualisation
                            </th>
                        </tr>
                    </thead>
                    <tbody className='divide-y divide-gray-200 bg-white'>
                        {MetricList.map((metric, index) => {
                            const isNavigation = metric._2DAction === wallsJob
                                || metric._2DAction === hullsValidation
                                || metric._3DAction === hullsValidation;
                            const ActionButton = isNavigation ? Button : ProjectEditButton;
                            return (
                                <tr key={index} className='divide-x divide-gray-200 even:bg-gray-50'>
                                    <td className='p-4 font-medium whitespace-nowrap text-gray-900 flex flex-row gap-x-4'>

                                        <div className='flex items-center'>
                                            <div className={`flex flex-row gap-x-0.5 ${!metric.isMagick && 'mr-5'}`}>
                                                <FontAwesomeIcon fixedWidth={true} icon={metric.icon} className='text-xl' />
                                                {metric.isMagick && <FontAwesomeIcon fixedWidth={true} icon={faWandMagicSparkles} className='text-sm self-end' />}
                                            </div>
                                        </div>

                                        <div>{metric.text}</div>
                                    </td>
                                    <td className='p-4 text-sm whitespace-nowrap text-gray-500'>
                                        <div className='flex flex-row gap-4 items-center'>
                                            {metric.is2DDone && (
                                                <FontAwesomeIcon fixedWidth={true} className='text-green-500' icon={faCheck} />
                                            )}
                                            {metric.is2DDone === undefined && (
                                                <FontAwesomeIcon fixedWidth={true} className='text-gray-300' icon={faHyphen} />
                                            )}
                                            {metric._2DAction && <ActionButton size='sm' className='w-18 ml-auto' disabled={!metric.is2D || (!isNavigation && (isProcessing || isProcessRunning))} onClick={() => {
                                                    if (!isNavigation) {
                                                        setIsProcessing(true);
                                                    }

                                                    metric._2DAction?.();
                                                }}><span className='flex-1 text-center'>{metric.actionText}</span></ActionButton>}
                                        </div>
                                    </td>
                                    <td className='p-4 text-sm whitespace-nowrap text-gray-500'>
                                        <div className='flex flex-row gap-4 items-center'>
                                            {metric.is3DDone && (
                                                <FontAwesomeIcon fixedWidth={true} className='text-green-500' icon={faCheck} />
                                            )}
                                            {metric.is3DDone === undefined && (
                                                <FontAwesomeIcon fixedWidth={true} className='text-gray-500' icon={faHyphen} />
                                            )}
                                            {metric._3DAction && <ActionButton size='sm' className='w-18 flex ml-auto' disabled={!metric.is3D || (!isNavigation && (isProcessing || isProcessRunning))} onClick={() => {
                                                    if (!isNavigation) {
                                                        setIsProcessing(true);
                                                    }

                                                    metric._3DAction?.();
                                                }}><span className='flex-1 text-center'>{metric.actionText}</span></ActionButton>}
                                        </div>
                                    </td>
                                    <td className='flex justify-center'>
                                        {metric.visualisationLink && (
                                            metric.visualisationLink.startsWith('http') ? (
                                                <ExternalLinkButton href={metric.visualisationLink} target='_blank'>
                                                    <FontAwesomeIcon icon={faEye} className='text-base' />
                                                </ExternalLinkButton>
                                            ) : (
                                                <LinkButton to={metric.visualisationLink}>
                                                    <FontAwesomeIcon icon={faEye} className='text-base' />
                                                </LinkButton>
                                            )
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </Card>
        </div>
    );
}

export default TakeOffProcesses;
